import { DEFAULT_SORT_OPTION } from './../../constants/sortOrder';
import {
  BUCKETS,
  type ProspectFilters,
} from '../../stores/JobApplicationStore/types';
import configurationService from '../configurationService';
import adCenterApiService from '../adCenterApiService';
import constructManualCandidateFormData from './constructManualCandidateFormData';
import type { CandidateFormValues } from '../../components/app/AddCandidate/AddCandidateForm';
import fetchJobApplicationSummariesByPositionId, {
  type JobApplicationSummaryResult,
} from '../../graphql/fetchJobApplicationSummariesByPositionId';
import {
  buildPropsForJobApplicationFetch,
  buildProspectsWithPaginationByJobIdInput,
  type ProspectsWithPaginationByJobIdProps,
} from '../../graphql/utils';
import retry from 'src/utils/manuallyFetchAddedCandidate';
import fetchProspectsWithPaginationByJobId, {
  type ProspectsResult,
} from 'src/graphql/fetchProspectsWithPaginationByJobId';
import { sleep } from 'src/utils/sleep';
import { navigateToSignInPageWithReturnUrl } from 'src/pages/CandidateManagementPage/utils/utils';
import { isValidId } from 'src/utils/advertiserIds';

const canFetchProspectsWithPaginationByjobId = (
  variables: ProspectsWithPaginationByJobIdProps,
) => isValidId(variables.advertiserId) && isValidId(variables.jobId.toString());

class JobApplicationService {
  async fetch(
    jobId: number,
    searchText: string,
    bucket: BUCKETS = BUCKETS.INBOX,
    sortBy = DEFAULT_SORT_OPTION.value,
    pageNumber: number = 1,
    filters?: ProspectFilters,
  ): Promise<ProspectsResult> {
    const variables: ProspectsWithPaginationByJobIdProps =
      buildProspectsWithPaginationByJobIdInput({
        jobId,
        sortBy,
        searchText,
        bucket,
        page: pageNumber,
        filters,
      });

    if (!canFetchProspectsWithPaginationByjobId(variables)) {
      navigateToSignInPageWithReturnUrl();
      return {
        nodes: [],
      };
    }

    return fetchProspectsWithPaginationByJobId(variables);
  }

  async exportApplicationsToCsv(positionId: number) {
    const body = {
      positionID: positionId,
    };

    return adCenterApiService.post(
      configurationService.getRelativeUrl('/api/v2/prospectData/getCsvAll'),
      body,
      (json) => json,
    );
  }

  async manuallyAddCandidate(
    candidate: CandidateFormValues,
    jobId: number,
    positionId: number,
    isSearchApiEnabled: boolean,
  ) {
    const data = constructManualCandidateFormData(positionId, candidate);
    const result = await adCenterApiService.post(
      configurationService.getRelativeUrl(
        '/api/v2/candidate/saveCandidateDetails',
      ),
      data,
      (json) => json,
      true,
    );

    if (isSearchApiEnabled) {
      const searchText = candidate.email;
      const variables: ProspectsWithPaginationByJobIdProps =
        buildProspectsWithPaginationByJobIdInput({
          jobId,
          searchText,
          bucket: BUCKETS.INBOX,
          sortBy: DEFAULT_SORT_OPTION.value,
          page: 1,
        });

      if (!canFetchProspectsWithPaginationByjobId(variables)) {
        navigateToSignInPageWithReturnUrl();
        return;
      }

      const findAddedCandidate = async () => {
        const prospect = await fetchProspectsWithPaginationByJobId(variables);
        if (prospect.nodes.length < 1) {
          throw new Error(
            `Failed to find manually added candidate with email ${searchText} in prospect list after retrying`,
          );
        }
      };
      await sleep(1000);
      return retry(5, findAddedCandidate);
    }

    return result;
  }

  async fetchAllJobApplications(
    positionId: number,
    searchText: string,
    bucket: BUCKETS = BUCKETS.INBOX,
    sortBy = DEFAULT_SORT_OPTION.value,
    filters?: ProspectFilters,
  ): Promise<JobApplicationSummaryResult> {
    const variables = buildPropsForJobApplicationFetch({
      positionId,
      bucket,
      searchText,
      sortBy,
      filters,
    });

    if (!isValidId(variables.advertiserId)) {
      navigateToSignInPageWithReturnUrl();
      return {
        nodes: [],
      };
    }

    return fetchJobApplicationSummariesByPositionId(variables);
  }
}

export default new JobApplicationService();
